// Client-side image compression. We can't easily resize on Cloudflare Workers
// without the paid Image.transform binding, so we cap on the way in.

export async function compressImage(file: File, maxEdge: number, quality = 0.92): Promise<File> {
  // Quick exit: if it's small enough, send as-is.
  const url = URL.createObjectURL(file)
  try {
    const img = await loadImage(url)
    const long = Math.max(img.naturalWidth, img.naturalHeight)
    if (long <= maxEdge) return file

    const scale = maxEdge / long
    const w = Math.round(img.naturalWidth * scale)
    const h = Math.round(img.naturalHeight * scale)
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')!
    ctx.drawImage(img, 0, 0, w, h)
    const outType = file.type === 'image/png' ? 'image/png' : 'image/jpeg'
    const blob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('canvas toBlob failed'))),
        outType,
        quality,
      ),
    )
    const ext = outType === 'image/png' ? 'png' : 'jpg'
    const baseName = file.name.replace(/\.[^.]+$/, '') || 'image'
    return new File([blob], `${baseName}.${ext}`, { type: outType, lastModified: Date.now() })
  } finally {
    URL.revokeObjectURL(url)
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = (e) => reject(e)
    img.src = src
  })
}
